import { useEffect, useRef, useState } from "react"

import OpendoorLoadRecovery from "../partners/opendoor/OpendoorLoadRecovery.jsx"
import { refreshD2cStaticAssets } from "./refreshD2cStaticAssets.js"

// The Opendoor host marks its document with <meta name="od-partner" content="opendoor"> (the application layout). This screen
// runs when the page chunk itself failed, so it cannot rely on the page having mounted: the marker is how it knows whose
// application it is (the page's own `experience` prop, when Inertia hands it over, says so too).
function onOpendoorHost(experience) {
  return experience?.key === "opendoor" || document.querySelector('meta[name="od-partner"]')?.getAttribute("content") === "opendoor"
}

// Eager bootstrap fallback: the journey never mounted, so there are no editable fields or saves to flush.
export function D2cPageLoadRecovery({ experience, supportPhone, homeUrl } = {}) {
  const mounted = useRef(true)
  const pending = useRef(false)
  const [busy, setBusy] = useState(false)
  const [reloadFailed, setReloadFailed] = useState(false)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const reload = async () => {
    if (pending.current) return
    pending.current = true
    setBusy(true)
    setReloadFailed(false)
    const selectedUrl = window.location.href
    try {
      await refreshD2cStaticAssets()
    } catch {
      // Best effort only: an asset refresh failure must not prevent an explicit native reload.
    }
    if (!mounted.current) return
    if (window.location.href !== selectedUrl) {
      pending.current = false
      setBusy(false)
      return
    }
    try {
      window.location.reload()
    } catch {
      pending.current = false
      setBusy(false)
      setReloadFailed(true)
    }
  }

  // Opendoor's: the kit's terminal-screen pattern, drawn by an eager component of its own (R2-15), not by the chunk that failed.
  if (onOpendoorHost(experience)) {
    return <OpendoorLoadRecovery busy={busy} reloadFailed={reloadFailed} onReload={reload} experience={experience} supportPhone={supportPhone} homeUrl={homeUrl} />
  }

  // Inline essentials keep this usable when a stylesheet was among the failed assets.
  return (
    <main style={{ minHeight: "100vh", padding: "32px 20px", background: "#f8fafc", color: "#273653", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ maxWidth: "480px", margin: "40px auto", lineHeight: 1.5 }}>
        <h1 style={{ fontSize: "24px", lineHeight: 1.25 }}>We couldn&apos;t load your application</h1>
        <p role="status" aria-live="polite">Reload to try again. Your application link will be kept.</p>
        <button type="button" disabled={busy} aria-busy={busy} onClick={reload} style={{ padding: "12px 20px", border: 0, borderRadius: "8px", background: "#0a5c9e", color: "white", fontSize: "16px", cursor: busy ? "wait" : "pointer" }}>
          {busy ? "Reopening application..." : "Reload application"}
        </button>
        {reloadFailed && <p role="alert">Please try reloading again.</p>}
      </div>
    </main>
  )
}
